import type { LaneId, VisibleCardViewV1, VisibleLaneV1 } from "@fate-kingdom/engine";
import type { PointerEventHandler, RefCallback } from "react";

import type { AlphaAttackAction } from "../drag/useAlphaFormationDrag";
import type { AlphaDisbandAction } from "../drag/useAlphaFieldDisbandDrag";
import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { getAlphaDisplayName } from "../presentation/alpha-card-presentation";
import { AlphaFieldLandCard } from "./AlphaFieldCard";
import { FormationStack } from "./FormationStack";

const ALPHA_LANE_NAMES: Readonly<Record<LaneId, string>> = {
  left: "Left Frontier",
  center: "Central Realm",
  right: "Right Frontier",
};

function fieldState(status: VisibleLaneV1["own"]["status"]): "active" | "committed" | "defeated" {
  if (status === "committed") return "committed";
  if (status === "defeated") return "defeated";
  return "active";
}

export function BattlefieldLane({
  laneId,
  lane,
  perspectivePlayerId,
  laneRef,
  targetMode,
  legal,
  hovered,
  dropState,
  placementPulse,
  disabled,
  dragLabel,
  attackActions,
  disbandActionsByCardId,
  attackSelected,
  onChooseLane,
  onSelectAttack,
  onFormationPointerDown,
  onInspectCards,
}: {
  readonly laneId: LaneId;
  readonly lane: VisibleLaneV1;
  readonly perspectivePlayerId: "player-1" | "player-2";
  readonly laneRef: RefCallback<HTMLButtonElement>;
  readonly targetMode: "own" | "opponent";
  readonly legal: boolean;
  readonly hovered: boolean;
  readonly dropState: "idle" | "hovered" | "legal" | "invalid";
  readonly placementPulse: boolean;
  readonly disabled: boolean;
  readonly dragLabel?: string | undefined;
  readonly attackActions?: ReadonlyMap<LaneId, AlphaAttackAction> | undefined;
  readonly disbandActionsByCardId?: ReadonlyMap<string, AlphaDisbandAction> | undefined;
  readonly attackSelected: boolean;
  readonly onChooseLane: () => void;
  readonly onSelectAttack: () => void;
  readonly onFormationPointerDown?: PointerEventHandler<HTMLButtonElement> | undefined;
  readonly onInspectCards: (cards: readonly VisibleCardViewV1[]) => void;
}) {
  const resultOwner =
    lane.result === "active" ? null : lane.result.startsWith("player-1") ? "player-1" : "player-2";
  const ownWon = resultOwner === perspectivePlayerId;
  const laneConcluded = lane.result !== "active";
  return (
    <article
      className={`alpha-battlefield-lane ${laneConcluded ? "is-concluded" : ""} ${legal ? "is-legal" : ""} ${hovered && legal ? "is-hovered" : ""}`}
      data-alpha-field-lane={laneId}
    >
      <header className="alpha-battlefield-lane__name">
        <span>{ALPHA_LANE_NAMES[laneId]}</span>
        {laneConcluded ? (
          <b>
            <AlphaGameIcon name="banner" aria-hidden="true" /> Claimed
          </b>
        ) : (
          <small>{laneId === "center" ? "Realm" : "Frontier"}</small>
        )}
      </header>

      <div className="alpha-battlefield-lane__side alpha-battlefield-lane__side--opponent">
        <FormationStack
          laneId={laneId}
          side={lane.opponent}
          owner="opponent"
          state={fieldState(lane.opponent.status)}
          onInspect={onInspectCards}
        />
        {lane.opponent.land === null ? (
          <div className="alpha-land-slot is-empty" aria-label={`Opponent ${laneId} Land empty`}>
            <AlphaGameIcon name="land" aria-hidden="true" />
          </div>
        ) : (
          <AlphaFieldLandCard
            land={lane.opponent.land}
            state={fieldState(lane.opponent.status)}
            onInspect={() => onInspectCards(lane.opponent.land!.cards)}
          />
        )}
      </div>

      <div className="alpha-battlefield-lane__conflict" aria-hidden="true">
        <span />
        <AlphaGameIcon name="attack" />
        <span />
      </div>

      <div className="alpha-battlefield-lane__side alpha-battlefield-lane__side--own">
        {lane.own.land === null ? (
          <div className="alpha-land-slot is-empty" aria-label={`Your ${laneId} Land empty`}>
            <AlphaGameIcon name="land" aria-hidden="true" />
          </div>
        ) : (
          <AlphaFieldLandCard
            land={lane.own.land}
            state={fieldState(lane.own.status)}
            onInspect={() => onInspectCards(lane.own.land!.cards)}
          />
        )}
        <FormationStack
          laneId={laneId}
          side={lane.own}
          owner="own"
          attackActions={attackActions}
          disbandActionsByCardId={disbandActionsByCardId}
          selected={attackSelected}
          state={fieldState(lane.own.status)}
          disabled={disabled}
          onSelectAttack={onSelectAttack}
          onPointerDown={onFormationPointerDown}
          onInspect={onInspectCards}
        />
      </div>

      <button
        ref={laneRef}
        className={`alpha-lane alpha-lane--${targetMode} ${legal ? "is-legal" : ""} ${hovered && legal ? "is-selected" : ""} ${placementPulse ? "is-placement-pulse" : ""} ${dropState !== "idle" ? "is-drag-target" : ""}`}
        type="button"
        data-alpha-lane-id={laneId}
        data-alpha-drop-state={dropState}
        disabled={disabled || !legal}
        onClick={onChooseLane}
        aria-label={
          legal
            ? `${dragLabel ?? "Choose"} ${ALPHA_LANE_NAMES[laneId]}`
            : `${ALPHA_LANE_NAMES[laneId]} is not a current legal target`
        }
      >
        <span>
          {dragLabel ??
            (targetMode === "opponent"
              ? `Target ${lane.opponent.land === null ? "Land" : getAlphaDisplayName(lane.opponent.land.cards[0]!)}`
              : lane.own.land === null
                ? "Found Land"
                : "Play here")}
        </span>
      </button>

      {laneConcluded ? (
        <div className={`alpha-lane-conclusion ${ownWon ? "is-own-win" : ""}`} aria-hidden="true">
          <AlphaGameIcon name="banner" />
        </div>
      ) : null}
    </article>
  );
}
